/* eslint-disable import/prefer-default-export */
import { useEffect, useRef } from 'react';
import { ROLES } from '@config/index';
import { getRolePermissions } from '@features/account/AccountSlice';
import { useAppDispatch, useAppSelector } from './redux.hooks';

/**
 * Re-fetches the current role's permissions once per app load (mount),
 * right after redux-persist rehydrates `currentUser` from localStorage.
 *
 * Without this, `currentUser.permissions` stays frozen at whatever was
 * fetched on the last login/profile-switch: an admin change to a permission
 * (e.g. "Can review documents" on a subcontract) never reaches an already
 * logged-in user's session until they log out and back in (KSS-2599).
 *
 * A browser refresh remounts the app, so this effectively makes "reload the
 * page" enough to pick up permission changes, without touching login or
 * switch-profile flows.
 *
 * IMPORTANT: uses `currentUser.kitchen`, not `currentUser.token`.
 * `assignRole` (dispatched on every login/profile-switch, see AccountSlice)
 * always resets `currentUser.token` back to `null` and moves the real active
 * JWT into `currentUser.kitchen` — that's also what `PrivateRoute` and the
 * RTK Query base headers (`BaseSlice`) read to authenticate requests. `token`
 * is a short-lived field only used transiently during the login handshake
 * itself. Gating on `token` here would silently never fire after the very
 * first login of the session.
 */
export const useRefreshPermissionsOnLoad = () => {
  const dispatch = useAppDispatch();
  const { currentUser } = useAppSelector((state) => state.account);
  const hasRefreshed = useRef(false);

  useEffect(() => {
    if (hasRefreshed.current) return;
    if (!currentUser?.kitchen) return;
    if (currentUser.role === ROLES.admin) return;

    hasRefreshed.current = true;
    dispatch(getRolePermissions(currentUser.kitchen));
  }, [currentUser?.kitchen, currentUser?.role, dispatch]);
};
